/**
 * `cellLabel` — how a cell is **named to a human** on this app's combat surfaces: the
 * spreadsheet-style label the combat board's own coordinate ruler prints, a letter for the column
 * and a number for the row (`G5`). Pure, no React, no Pixi.
 *
 * **This module is the single source of that label, and the board
 * (`routes/home/pixiCombatBoard.ts`) is its other consumer.** A panel that confidently names a cell
 * the board labels differently is worse than one that prints raw coordinates — don't fork this.
 *
 * **The origin is the board's, and it is not the engine's.** The board draws a cell at
 * `(x * CELL, y * CELL)` and its ruler labels column `c` as `columnLabel(c)` and row `r` as
 * `String(r + 1)` (`pixiCombatBoard.ts`'s `buildBoard`). So engine column `0` is **`A`** and engine
 * row `0` is **`1`** — the column letter is zero-derived, the row number is one-based. Both halves
 * are read off the ruler's own code; getting the row off by one would make every label point at a
 * plausible-looking wrong cell.
 *
 * **Square grids only, deliberately.** The board is a square-lattice renderer (its cells are `{x,y}`
 * and its ruler is a `cols`/`rows` loop), so there is no lettered ruler for a hex encounter to
 * match — a hex cell falls back to `world.key`.
 */
import { encounterGridMode, gridFamily, worldFor, type Cell, type EncounterState } from "@lancer-vtt/domain";

/**
 * Spreadsheet-style column label (0→A … 25→Z, 26→AA, 27→AB, …). Bijective base-26: past `Z` it wraps
 * to two letters exactly the way a spreadsheet does, so the panel and the ruler agree past column 26
 * too. `pixiCombatBoard.ts` imports this to draw the ruler itself.
 */
export function columnLabel(index: number): string {
  let n = index;
  let label = "";
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

/**
 * Name `cell` the way the board's ruler labels it — `"G5"` for the square cell `{x:6,y:4}`.
 *
 * The square-vs-hex decision reads the **encounter's** grid mode, never `cell.grid`: cells produced
 * by the world geometry (a reachable destination, a projected anchor — which is exactly what the
 * advisor hands us) are structurally right but **may not carry the `grid` tag at runtime**, by
 * design (see `Cell`'s own doc in `packages/domain/src/encounter/types.ts` + the controlled port
 * cast in `world.ts`). Branching on the tag would silently drop those to the raw-key fallback. The
 * `"x" in cell` that follows is narrowing only — the decision has already been made.
 */
export function cellLabel(state: EncounterState, cell: Cell): string {
  if (gridFamily(encounterGridMode(state)) === "square" && "x" in cell) {
    return `${columnLabel(cell.x)}${cell.y + 1}`;
  }
  return worldFor(state).key(cell);
}
